import React, { useEffect, useState, useCallback, useRef } from 'react';
import { announcementsAPI, usersAPI } from '../services/api';
import { Table, Badge, Btn, Modal, Card, Pagination, useToast, Input, Select, Spinner } from '../components/UI';

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Announcements() {
  const { show, ToastEl } = useToast();
  const [items, setItems]       = useState([]);
  const [roles, setRoles]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [meta, setMeta]         = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await announcementsAPI.list({ page, limit: 15 });
      setItems(data.data);
      setMeta(data.pagination);
    } catch { show('Failed to load announcements', 'error'); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { usersAPI.listRoles().then(r => setRoles(r.data.data)); }, []);

  async function sendDraft(id, e) {
    e.stopPropagation();
    try {
      await announcementsAPI.send(id);
      show('Announcement sent successfully');
      load();
    } catch { show('Failed to send', 'error'); }
  }

  function typeIcon(type) {
    if (type === 'voice') return '🔊';
    if (type === 'both')  return '📢';
    return '📝';
  }

  const columns = [
    { key: 'title', label: 'Title', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{typeIcon(row.type)}</span>
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          {row.body && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.body}
            </div>
          )}
        </div>
      </div>
    )},
    { key: 'type',       label: 'Type',   render: v => <Badge label={v} type={v}/> },
    { key: 'target',     label: 'Target', render: v => <Badge label={v === 'all' ? 'All users' : 'Role specific'} type={v}/> },
    { key: 'status',     label: 'Status', render: v => <Badge label={v} type={v}/> },
    { key: 'created_by', label: 'By',     render: v => <span style={{ fontSize: 12 }}>{v}</span> },
    { key: 'created_at', label: 'Date',   render: v => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
    { key: 'actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn size="sm" variant="outline" onClick={e => { e.stopPropagation(); setViewItem(row); }}>View</Btn>
        {row.status === 'draft' && (
          <Btn size="sm" variant="gold" onClick={e => sendDraft(row.id, e)}>Send now</Btn>
        )}
      </div>
    )},
  ];

  return (
    <div style={{ padding: '28px 32px', animation: 'fadeIn .35s ease' }}>
      {ToastEl}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700 }}>Announcements</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>Send text and voice notifications to users or specific roles.</p>
        </div>
        <Btn variant="primary" onClick={() => setShowCreate(true)}>+ New announcement</Btn>
      </div>

      <Card>
        <Table
          columns={columns}
          data={items}
          loading={loading}
          emptyMsg="No announcements yet"
        />
        <Pagination page={page} pages={meta.pages} total={meta.total} limit={15} onChange={setPage}/>
      </Card>

      {/* View modal */}
      {viewItem && (
        <ViewModal
          item={viewItem}
          onClose={() => setViewItem(null)}
          onSend={() => { sendDraft(viewItem.id, { stopPropagation: () => {} }); setViewItem(null); }}
          onRead={() => load()}
        />
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateModal
          roles={roles}
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); load(); show('Announcement created'); }}
          onError={msg => show(msg, 'error')}
        />
      )}
    </div>
  );
}

// ── View Modal ────────────────────────────────────────────────────────────────
function ViewModal({ item, onClose, onSend, onRead }) {
  const audioRef            = useRef(null);
  const [audioBlobUrl, setAudioBlobUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError]     = useState('');
  const [markedRead, setMarkedRead]     = useState(item.is_read);
  const token = localStorage.getItem('accessToken');

  // Load audio blob if this announcement has audio
  useEffect(() => {
    if (item.type === 'text' || !item.audio_url) return;

    let objectUrl = null;
    setAudioLoading(true);
    setAudioError('');

    fetch(`/api/announcements/${item.id}/audio`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        const typed   = new Blob([blob], { type: 'audio/mpeg' });
        objectUrl     = URL.createObjectURL(typed);
        setAudioBlobUrl(objectUrl);
      })
      .catch(e => setAudioError(e.message || 'Failed to load audio'))
      .finally(() => setAudioLoading(false));

    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [item.id, item.type, item.audio_url]);

  // Auto mark as read when opened
  useEffect(() => {
    if (!item.is_read && item.status === 'sent') {
      announcementsAPI.markRead(item.id)
        .then(() => { setMarkedRead(true); onRead(); })
        .catch(() => {});
    }
  }, [item.id]);

  function typeLabel(type) {
    return { text: 'Text', voice: 'Voice', both: 'Text + Voice' }[type] || type;
  }

  function typeIcon(type) {
    return { text: '📝', voice: '🔊', both: '📢' }[type] || '📝';
  }

  return (
    <Modal title="" onClose={onClose} width={580}>

      {/* Header */}
      <div style={{
        margin: '-20px -20px 20px',
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface2)',
        display: 'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <span style={{ fontSize: 28, flexShrink: 0 }}>{typeIcon(item.type)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.3 }}>{item.title}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            <Badge label={item.status}  type={item.status}/>
            <Badge label={typeLabel(item.type)} type={item.type}/>
            <Badge label={item.target === 'all' ? 'All users' : 'Role specific'} type={item.target}/>
            {markedRead && <Badge label="Read" type="resolved"/>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {item.status === 'draft' && (
            <Btn size="sm" variant="gold" onClick={onSend}>Send now</Btn>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text3)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Text body */}
        {(item.type === 'text' || item.type === 'both') && item.body && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 8 }}>
              Message
            </div>
            <div style={{
              background: 'var(--surface2)', borderRadius: 'var(--r)',
              padding: '14px 16px', fontSize: 14, lineHeight: 1.75,
              color: 'var(--text)', whiteSpace: 'pre-wrap',
              borderLeft: '3px solid var(--accent)',
            }}>
              {item.body}
            </div>
          </div>
        )}

        {/* Voice-only with no body */}
        {item.type === 'voice' && !item.body && (
          <div style={{ fontSize: 13, color: 'var(--text3)', fontStyle: 'italic' }}>
            This is a voice announcement — listen to the audio below.
          </div>
        )}

        {/* Audio player */}
        {(item.type === 'voice' || item.type === 'both') && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 8 }}>
              Voice announcement
            </div>

            {audioLoading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px', background: 'var(--surface2)', borderRadius: 'var(--r)' }}>
                <Spinner size={16}/>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>Loading audio…</span>
              </div>
            )}

            {!audioLoading && audioError && (
              <div style={{ background: 'var(--red-bg)', border: '1px solid #f5c6c2', borderRadius: 'var(--r)', padding: '12px 14px', fontSize: 12, color: 'var(--red)' }}>
                Could not load audio: {audioError}
              </div>
            )}

            {!audioLoading && !audioError && audioBlobUrl && (
              <div style={{ background: 'var(--surface2)', borderRadius: 'var(--r2)', padding: '16px 20px' }}>
                <audio
                  ref={audioRef}
                  controls
                  src={audioBlobUrl}
                  style={{ width: '100%', outline: 'none', borderRadius: 8 }}
                >
                  Your browser does not support audio playback.
                </audio>
              </div>
            )}

            {!audioLoading && !audioError && !audioBlobUrl && !item.audio_url && (
              <div style={{ background: 'var(--surface2)', borderRadius: 'var(--r)', padding: '12px 14px', fontSize: 12, color: 'var(--text3)' }}>
                No audio file attached to this announcement.
              </div>
            )}
          </div>
        )}

        {/* Metadata */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          {[
            { label: 'Created by', value: item.created_by },
            { label: 'Date',       value: new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) },
            { label: 'Target',     value: item.target === 'all' ? 'All users' : 'Specific roles' },
            { label: 'Type',       value: typeLabel(item.type) },
          ].map(({ label, value }) => (
            <div key={label}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 13 }}>{value}</div>
            </div>
          ))}
        </div>

      </div>
    </Modal>
  );
}

// ── Create Modal ──────────────────────────────────────────────────────────────
function CreateModal({ roles, onClose, onSuccess, onError }) {
  const [form, setForm] = useState({
    title: '', body: '', type: 'text', target: 'all', roleIds: [], send_now: true,
  });
  const [audio, setAudio] = useState(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.title.trim()) return onError('Title is required');
    if (form.target === 'role_specific' && !form.roleIds.length) return onError('Select at least one role');
    if ((form.type === 'voice' || form.type === 'both') && !audio) return onError('Please attach an audio file');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title',    form.title);
      fd.append('body',     form.body);
      fd.append('type',     form.type);
      fd.append('target',   form.target);
      fd.append('send_now', form.send_now);
      if (form.target === 'role_specific') {
        form.roleIds.forEach(rid => fd.append('roleIds[]', rid));
      }
      if (audio && (form.type === 'voice' || form.type === 'both')) {
        fd.append('audio', audio);
      }
      await announcementsAPI.create(fd);
      onSuccess();
    } catch (e) { onError(e.response?.data?.message || 'Failed to create announcement'); }
    finally { setSaving(false); }
  }

  function toggleRole(id, checked) {
    const rid = Number(id);
    setForm(f => ({
      ...f,
      roleIds: checked ? [...f.roleIds, rid] : f.roleIds.filter(x => x !== rid),
    }));
  }

  return (
    <Modal title="New announcement" onClose={onClose} width={540}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        <Input
          label="Title *"
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Upcoming Majlis Notice"
        />

        <div>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>
            Message body {form.type !== 'voice' && '*'}
          </label>
          <textarea
            value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
            placeholder="Assalamu Alaikum. Please note that…"
            style={{
              width: '100%', minHeight: 100,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 13,
              resize: 'vertical', fontFamily: 'var(--sans)', color: 'var(--text)',
              outline: 'none', lineHeight: 1.65,
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Select label="Announcement type" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
            <option value="text">📝 Text only</option>
            <option value="voice">🔊 Voice only</option>
            <option value="both">📢 Text + Voice</option>
          </Select>
          <Select label="Send to" value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))}>
            <option value="all">All users</option>
            <option value="role_specific">Specific roles</option>
          </Select>
        </div>

        {(form.type === 'voice' || form.type === 'both') && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>
              Audio file *
            </label>
            <input type="file" accept="audio/*" onChange={e => setAudio(e.target.files[0])} style={{ fontSize: 13 }}/>
            {audio && (
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                {audio.name} · {(audio.size / 1024 / 1024).toFixed(2)} MB
              </div>
            )}
          </div>
        )}

        {form.target === 'role_specific' && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', marginBottom: 8 }}>Target roles *</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {roles.filter(r => r.name !== 'Admin').map(r => {
                const checked = form.roleIds.includes(Number(r.id));
                return (
                  <label key={r.id} style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
                    background: checked ? 'var(--accent-light)' : 'var(--surface2)',
                    border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--r)', padding: '5px 12px', transition: 'all .12s',
                  }}>
                    <input
                      type="checkbox"
                      style={{ accentColor: 'var(--accent)' }}
                      checked={checked}
                      onChange={e => toggleRole(r.id, e.target.checked)}
                    />
                    {r.name}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            style={{ accentColor: 'var(--accent)', width: 14, height: 14, marginTop: 2, flexShrink: 0 }}
            checked={form.send_now}
            onChange={e => setForm(f => ({ ...f, send_now: e.target.checked }))}
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Send immediately</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
              Uncheck to save as a draft and send later.
            </div>
          </div>
        </label>

        <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
          <Btn onClick={submit} disabled={saving}>
            {saving ? <Spinner size={14} color="#fff"/> : (form.send_now ? '📢 Send now' : 'Save draft')}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>

      </div>
    </Modal>
  );
}
